/**
 * Paginated list endpoints (`/leads`, `/tasks`, …) answer with
 * `{ data, current_page, page_size, total_pages, total }` and a **default
 * `page_size` of 20**.
 *
 * Any client-side aggregate — a sum, an average, a ranking, a "top N" — is
 * wrong if it stops after the first page: it silently describes a slice while
 * looking like a total. `fetchAllPages` walks the pages instead.
 *
 * See docs/dashboards-endpoints.md ("counts-only report") for why the sales
 * dashboards aggregate client-side in the first place.
 */

/** Pages fetched at most, so one partner with a huge history can't hang a card. */
export const DEFAULT_MAX_PAGES = 10;

/**
 * Counting every matching row is the expensive part of a list request, so
 * lists ask the backend to skip or cap it:
 * - `skip_total_size=1` — `total` / `total_pages` come back `null`; page with
 *   `links.next`. For lists that never show a count.
 * - `capped_total_size=1` — `total` is exact below 100 rows and the string
 *   `'+99'` from there on. For lists that show a count (kanban columns, group
 *   headers, badges); render it with `formatListTotal`.
 */
export const SKIP_TOTAL_SIZE = { skip_total_size: 1 } as const;
export const CAPPED_TOTAL_SIZE = { capped_total_size: 1 } as const;

/** `total` as list endpoints send it: exact, `'+99'` (capped) or `null` (skipped). */
export type ListTotal = number | string | null | undefined;

export type ParsedListTotal = {
  /** The exact count, or the cap (99) when `capped`. */
  count: number;
  /** True when there are more rows than `count`. */
  capped: boolean;
};

export function parseListTotal(total: ListTotal): ParsedListTotal | null {
  if (total === null || total === undefined || total === '') return null;
  const capped = typeof total === 'string' && total.trim().startsWith('+');
  const count = Number(typeof total === 'string' ? total.replace('+', '') : total);
  return Number.isFinite(count) ? { count, capped } : null;
}

/** `'+99'` → `99+`, exact counts unchanged, `''` when the total is unknown. */
export function formatListTotal(total: ListTotal): string {
  const parsed = parseListTotal(total);
  if (!parsed) return '';
  return parsed.capped ? `${parsed.count}+` : String(parsed.count);
}

/**
 * Turns a capped list's `links.next` into an uncounted one.
 *
 * The backend clamps the page in capped links to the cap's last page (5 at 20
 * rows a page), so from page 6 on `links.next` points back at page 6 and a
 * column following it never grows. The count only matters on the first page
 * anyway, so follow-up pages switch to `skip_total_size`, whose links are
 * correct. First-page links keep their count.
 */
export function withSkippedTotal(url: string): string {
  try {
    const parsed = new URL(url);
    const page = Number(parsed.searchParams.get('page')) || 1;
    if (page <= 1 || !parsed.searchParams.has('capped_total_size')) return url;
    parsed.searchParams.delete('capped_total_size');
    parsed.searchParams.set('skip_total_size', '1');
    return parsed.toString();
  } catch {
    return url;
  }
}

type PageBody = {
  links?: { next?: string | null };
  total_pages?: number | null;
  current_page?: number | null;
};

/**
 * Prefer `links.next` — `skip_total_size=1` leaves `total` / `total_pages` null.
 * Fall back to `total_pages > current_page` for endpoints that still send counts.
 */
export function hasNextPage(body?: PageBody | null): boolean {
  if (body?.links?.next) return true;
  const totalPages = body?.total_pages;
  const currentPage = body?.current_page;
  return (
    typeof totalPages === 'number' &&
    typeof currentPage === 'number' &&
    totalPages > currentPage
  );
}

export type PagedResult<T> = {
  items: T[];
  /** True when `maxPages` cut the walk short — the aggregate understates. */
  truncated: boolean;
  /** Server-reported row count, when it sends one. */
  total: number | null;
};

/**
 * Collects every page of a paginated endpoint.
 *
 * @param fetchPage receives the 1-based page number and must resolve the raw
 *   `request` result (`{data: {data, total_pages, …}}`).
 */
export const fetchAllPages = async <T>(
  fetchPage: (page: number) => Promise<any>,
  { maxPages = DEFAULT_MAX_PAGES }: { maxPages?: number } = {},
): Promise<PagedResult<T>> => {
  const items: T[] = [];
  let total: number | null = null;
  let morePages = true;
  let page = 1;

  // Sequential on purpose: each page is what tells us whether there is another.
  for (; morePages && page <= maxPages; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result: any = await fetchPage(page);
    const body = result?.data;
    items.push(...((body?.data || []) as T[]));
    morePages = hasNextPage(body);
    if (page === 1) {
      const parsed = parseListTotal(body?.total);
      total = parsed && !parsed.capped ? parsed.count : null;
    }
  }

  return { items, truncated: morePages, total };
};
